/**
 * Homepage arrangement — the page as one ordered list.
 *
 * Sections and banners are numbered in separate tables, but they render
 * interleaved. Typing numbers on two screens meant merging two sequences in
 * your head to guess the result. Here the list *is* the page: drag a block,
 * save, and the backend rewrites both tables to a clean 1..n sequence.
 *
 * Dragging between zones is meaningful too — a banner dropped in Footer
 * becomes a footer banner.
 *
 * Uses native HTML5 drag events, the same approach as the product picker in
 * DynamicSectionForm, rather than pulling in a drag-and-drop library.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { GripVertical, Image as ImageIcon, LayoutGrid, Save } from "lucide-react";
import swal from "sweetalert";
import axiosInstance from "../../../redux/api";
import HomePageTabs from "../Banners/HomePageTabs";
import "./HomePageCRM.css";

const ZONES = [
  { key: "top", label: "Hero", hint: "Full-width carousel at the top of the page" },
  { key: "middle", label: "Body", hint: "Product sections and banners, in order" },
  { key: "bottom", label: "Footer", hint: "Strip just above the footer" },
];

const EMPTY_LAYOUT = { top: [], middle: [], bottom: [] };

const blockKey = (block) => `${block.type}-${block.id}`;

const HomepageArrange = () => {
  const [layout, setLayout] = useState(EMPTY_LAYOUT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // {zone, index} of the block being dragged. A ref, not state: it changes on
  // every dragover and re-rendering the whole page on each one makes the drag
  // stutter.
  const dragFrom = useRef(null);

  const fetchLayout = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get("admin/homepage/layout");
      const response = data?.response || {};
      setLayout({
        top: response.top || [],
        middle: response.middle || [],
        bottom: response.bottom || [],
      });
      setDirty(false);
    } catch {
      swal({ text: "Could not load the homepage layout", icon: "error" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLayout();
  }, [fetchLayout]);

  const handleDragStart = (zone, index) => {
    dragFrom.current = { zone, index };
  };

  // Moves the dragged block to the hovered slot as you go, so the list shows
  // the result before you let go.
  const handleDragEnter = (zone, index) => {
    const from = dragFrom.current;
    if (!from) return;
    if (from.zone === zone && from.index === index) return;

    const block = layout[from.zone][from.index];
    if (!block) return;

    // A section has no home outside the body — the backend rejects it, so
    // don't let the drop look like it worked.
    if (block.type === "section" && zone !== "middle") return;

    setLayout((current) => {
      const next = { ...current };
      next[from.zone] = [...current[from.zone]];
      const [moved] = next[from.zone].splice(from.index, 1);
      next[zone] = from.zone === zone ? next[from.zone] : [...current[zone]];
      next[zone].splice(index, 0, moved);
      return next;
    });
    dragFrom.current = { zone, index };
    setDirty(true);
  };

  const handleDragEnd = () => {
    dragFrom.current = null;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = ZONES.reduce((acc, zone) => {
        acc[zone.key] = layout[zone.key].map((block) => ({
          type: block.type,
          id: block.id,
        }));
        return acc;
      }, {});
      await axiosInstance.put("admin/homepage/layout", payload);
      swal({ text: "Homepage order saved", icon: "success", timer: 1400 });
      fetchLayout();
    } catch (error) {
      swal({
        text: error?.response?.data?.message || "Could not save the order",
        icon: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="ds-container">
        <HomePageTabs />
        <div className="ds-empty-state" style={{ height: 200 }}>Loading layout...</div>
      </div>
    );
  }

  return (
    <div className="ds-container">
      <HomePageTabs />

      <div className="ds-header">
        <div>
          <h4>Arrange Homepage</h4>
          <p>Drag blocks into the order customers will see them.</p>
        </div>
        <button
          className="ds-btn-primary"
          onClick={handleSave}
          disabled={saving || !dirty}
        >
          <Save size={15} style={{ marginInlineEnd: 6 }} />
          {saving ? "Saving..." : dirty ? "Save order" : "Saved"}
        </button>
      </div>

      <div className="ds-arrange">
        {ZONES.map((zone) => (
          <div key={zone.key} className="ds-arrange__zone">
            <div className="ds-arrange__zone-head">
              <h5>{zone.label}</h5>
              <span>{zone.hint}</span>
            </div>

            {layout[zone.key].length === 0 ? (
              // Still a drop target: an empty zone is how a banner gets moved
              // into it.
              <div
                className="ds-empty-state"
                onDragEnter={() => handleDragEnter(zone.key, 0)}
                onDragOver={(e) => e.preventDefault()}
              >
                Nothing here — drag a banner in
              </div>
            ) : (
              <div className="ds-arrange__list">
                {layout[zone.key].map((block, index) => (
                  <div
                    key={blockKey(block)}
                    className={`ds-arrange__block ds-arrange__block--${block.type}${
                      block.is_active ? "" : " ds-arrange__block--off"
                    }`}
                    draggable
                    onDragStart={() => handleDragStart(zone.key, index)}
                    onDragEnter={() => handleDragEnter(zone.key, index)}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => e.preventDefault()}
                  >
                    <GripVertical size={15} className="ds-arrange__grip" />
                    <span className="ds-arrange__pos">{index + 1}</span>
                    {block.type === "banner" ? (
                      <ImageIcon size={15} className="ds-arrange__icon" />
                    ) : (
                      <LayoutGrid size={15} className="ds-arrange__icon" />
                    )}
                    {block.type === "banner" && block.image && (
                      <img
                        src={block.image}
                        alt=""
                        className="ds-arrange__thumb"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    )}
                    <span className="ds-arrange__label">{block.label}</span>
                    <span className="ds-badge ds-badge--surface">{block.surface}</span>
                    {!block.is_active && (
                      <span className="ds-badge ds-badge--inactive">Inactive</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default HomepageArrange;
